// Mentorship.js
import React, { useState, useEffect } from 'react';
import { Star, Calendar, Clock, Users, Award, X, Check, ArrowRight, Search, Filter, Heart, ChevronDown, MapPin, Video, MessageCircle, Shield, TrendingUp } from 'lucide-react';
import '../styles/Mentorship.css';

const MentorshipPlatform = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [sessionType, setSessionType] = useState("1-on-1");
  const [bookingStep, setBookingStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [sortBy, setSortBy] = useState("rating");
  const [favorites, setFavorites] = useState(new Set());
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const filters = [
    "All",
    "Cracked Interview", 
    "Skill-based",
    "Experience Level",
    "Company Background",
    "Offer Type",
    "Session Type"
  ];

  const testimonials = [
    {
      name: "Rahul Verma",
      role: "Software Engineer at Google",
      text: "The mentorship helped me crack my Google interview. The system design sessions were incredibly valuable!",
      rating: 5
    },
    {
      name: "Sneha Patel",
      role: "Data Scientist at Microsoft",
      text: "Found my perfect mentor who guided me through the entire interview process. Highly recommended!",
      rating: 5
    },
    {
      name: "Arjun Kumar",
      role: "Product Manager at Amazon",
      text: "The mock interviews were game-changers. Got my dream job within 3 months of mentorship.",
      rating: 5
    }
  ];

  const mentorships = [
    {
      id: 1,
      mentor: "Ravi Kumar",
      company: "Microsoft",
      role: "Senior Software Engineer",
      offerType: "Full Time Offer",
      experience: "5 Years",
      skill: "DSA + System Design",
      session: "1:1 Mentorship",
      category: "Cracked Interview",
      activelyMentoring: true,
      rating: 4.9,
      totalSessions: 150,
      price: 2500,
      avatar: "RK",
      expertise: ["System Design", "DSA", "Interview Prep"],
      availability: ["Mon", "Tue", "Thu", "Sat"],
      location: "Bangalore",
      responseTime: "< 2 hours",
      successRate: "94%",
      bio: "Helped 100+ engineers crack FAANG interviews with proven strategies and personalized guidance.",
      languages: ["English", "Hindi"],
      nextAvailable: "Today 3:00 PM"
    },
    {
      id: 2,
      mentor: "Ananya Sharma",
      company: "Google",
      role: "Senior Data Scientist",
      offerType: "Full Time Offer",
      experience: "6 Years",
      skill: "Machine Learning, Python, AI",
      session: "Group Session",
      category: "Skill-based",
      activelyMentoring: true,
      rating: 4.8,
      totalSessions: 200,
      price: 3000,
      avatar: "AS",
      expertise: ["Machine Learning", "Python", "Data Science"],
      availability: ["Wed", "Thu", "Fri", "Sun"],
      location: "Hyderabad",
      responseTime: "< 1 hour",
      successRate: "91%",
      bio: "ML expert with experience in building production AI systems at scale.",
      languages: ["English", "Telugu", "Hindi"],
      nextAvailable: "Tomorrow 10:00 AM"
    },
    {
      id: 3,
      mentor: "Kunal Mehta",
      company: "Amazon",
      role: "Backend Developer",
      offerType: "Internship → FTO",
      experience: "4 Years",
      skill: "Java, Spring Boot, AWS",
      session: "Mock Interview",
      category: "Company Background",
      activelyMentoring: true,
      rating: 4.7,
      totalSessions: 80,
      price: 2000,
      avatar: "KM",
      expertise: ["Java", "Spring Boot", "AWS", "Backend"],
      availability: ["Mon", "Wed", "Fri", "Sat"],
      location: "Mumbai",
      responseTime: "< 3 hours",
      successRate: "89%",
      bio: "Backend specialist helping developers master system architecture and cloud technologies.",
      languages: ["English", "Hindi", "Gujarati"],
      nextAvailable: "Today 6:00 PM"
    },
    {
      id: 4,
      mentor: "Priya Patel",
      company: "Flipkart",
      role: "Frontend Lead",
      offerType: "Full Time Offer",
      experience: "7 Years",
      skill: "React, JavaScript, UI/UX",
      session: "1:1 Mentorship",
      category: "Skill-based",
      activelyMentoring: true,
      rating: 4.9,
      totalSessions: 120,
      price: 2800,
      avatar: "PP",
      expertise: ["React", "JavaScript", "Frontend", "UI/UX"],
      availability: ["Tue", "Thu", "Sat", "Sun"],
      location: "Pune",
      responseTime: "< 1 hour",
      successRate: "96%",
      bio: "Frontend architect with expertise in modern web technologies and user experience design.",
      languages: ["English", "Hindi", "Marathi"],
      nextAvailable: "Today 4:00 PM"
    },
    {
      id: 5,
      mentor: "Arjun Singh",
      company: "Meta",
      role: "Product Manager",
      offerType: "Full Time Offer",
      experience: "8 Years",
      skill: "Product Strategy, Analytics",
      session: "Group Session",
      category: "Experience Level",
      activelyMentoring: true,
      rating: 4.8,
      totalSessions: 95,
      price: 3500,
      avatar: "AS",
      expertise: ["Product Management", "Strategy", "Analytics"],
      availability: ["Mon", "Tue", "Wed", "Fri"],
      location: "Delhi",
      responseTime: "< 2 hours",
      successRate: "92%",
      bio: "Product leader helping aspiring PMs understand user psychology and business strategy.",
      languages: ["English", "Hindi", "Punjabi"],
      nextAvailable: "Tomorrow 2:00 PM"
    }
  ];

  // Filter and sort mentors
  const filteredMentorships = mentorships
    .filter(mentorship => {
      const matchesFilter = activeFilter === "All" || mentorship.category === activeFilter;
      const matchesSearch = mentorship.mentor.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           mentorship.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           mentorship.expertise.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesPrice = mentorship.price >= priceRange[0] && mentorship.price <= priceRange[1];
      return matchesFilter && matchesSearch && matchesPrice;
    })
    .sort((a, b) => {
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "price-low") return a.price - b.price;
      if (sortBy === "price-high") return b.price - a.price;
      if (sortBy === "experience") return parseInt(b.experience) - parseInt(a.experience);
      return 0;
    });

  const toggleFavorite = (mentorId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(mentorId)) {
      newFavorites.delete(mentorId);
    } else {
      newFavorites.add(mentorId);
    }
    setFavorites(newFavorites);
  };

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleBookSession = (mentor) => {
    setSelectedMentor(mentor);
    setShowBookingModal(true);
    setBookingStep(1);
  };

  const closeModal = () => {
    setShowBookingModal(false);
    setSelectedMentor(null);
    setSelectedDate("");
    setSelectedTime("");
    setSessionType("1-on-1");
    setBookingStep(1);
  };

  const proceedToNext = () => {
    setBookingStep(2);
  };

  const confirmBooking = () => {
    setBookingStep(3);
  };

  const timeSlots = ["09:00 AM", "10:00 AM", "11:00 AM", "02:00 PM", "03:00 PM", "04:00 PM", "07:00 PM", "08:00 PM"];

  return (
    <div className="mentorship-platform">
      {/* Enhanced Hero Section */}
      <section className="hero-section1">
        <div className="hero-overlay"></div>
        <div className="hero-gradient"></div>
        
        <div className="hero-content">
          <div className="hero-grid">
            <div className="hero-text">
              <div className="hero-badge">
                <Award className="badge-icon" />
                <span>Premium Mentorship Platform</span>
              </div>
              
              <div className="hero-titles">
                <h1 className="hero-title">
                  Accelerate Your Career with
                  <span className="hero-title-gradient">
                    Expert Mentors
                  </span>
                </h1>
                
                <p className="hero-description">
                  Connect with industry professionals who've cracked interviews at top tech companies. 
                  Get personalized guidance, mock interviews, and career advice to land your dream job.
                </p>
              </div>

              <div className="hero-stats">
                <div className="hero-stat">
                  <div className="stat-icon-wrapper">
                    <Users className="stat-icon1" />
                  </div>
                  <div>
                    <p className="stat-number">500+</p>
                    <p className="stat-label">Expert Mentors</p>
                  </div>
                </div>
                <div className="hero-stat">
                  <div className="stat-icon-wrapper">
                    <Star className="stat-icon1" />
                  </div>
                  <div>
                    <p className="stat-number">4.8/5</p>
                    <p className="stat-label">Average Rating</p>
                  </div>
                </div>
                <div className="hero-stat">
                  <div className="stat-icon-wrapper">
                    <TrendingUp className="stat-icon1" />
                  </div>
                  <div>
                    <p className="stat-number">94%</p>
                    <p className="stat-label">Success Rate</p>
                  </div>
                </div>
              </div>

              <div className="hero-actions">
                <button className="btn-primary">
                  Find Your Mentor
                </button>
                <button className="btn-secondary">
                  Watch Demo
                </button>
              </div>
            </div>

            {/* Testimonial Carousel */}
            <div className="testimonial-section">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="star-filled" />
                    ))}
                  </div>
                  
                  <blockquote className="testimonial-text">
                    "{testimonials[currentTestimonial].text}"
                  </blockquote>
                  
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonials[currentTestimonial].name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="author-name">{testimonials[currentTestimonial].name}</p>
                      <p className="author-role">{testimonials[currentTestimonial].role}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Testimonial indicators */}
              <div className="testimonial-indicators">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`testimonial-dot ${index === currentTestimonial ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Search and Filters */}
      <section className="search-section">
        <div className="search-container">
          <div className="search-controls">
            {/* Search Bar */}
            <div className="search-bar">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search mentors, skills, companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Filter Controls */}
            <div className="filter-controls">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="filter-btn"
              >
                <Filter className="filter-icon" />
                Filters
                <ChevronDown className={`chevron-icon ${showFilters ? 'rotated' : ''}`} />
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="rating">Sort by Rating</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="experience">Experience</option>
              </select>
            </div>
          </div>

          {/* Expandable Filter Panel */}
          {showFilters && (
            <div className="filter-panel">
              <div className="filter-grid">
                <div className="filter-category">
                  <label className="filter-label">Category</label>
                  <div className="filter-buttons">
                    {filters.map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setActiveFilter(filter)}
                        className={`category-btn ${activeFilter === filter ? 'active' : ''}`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-category">
                  <label className="filter-label">
                    Price Range: ₹{priceRange[0]} - ₹{priceRange[1]}
                  </label>
                  <div className="price-range">
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="100"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="range-slider"
                    />
                    <div className="price-inputs">
                      <input
                        type="number"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                        className="price-input"
                        min="0"
                      />
                      <span>-</span>
                      <input
                        type="number"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                        className="price-input"
                        max="5000"
                      />
                    </div>
                  </div>
                </div>

                <div className="filter-category">
                  <label className="filter-label">Quick Filters</label>
                  <div className="quick-filters">
                    <label className="checkbox-label">
                      <input type="checkbox" className="checkbox" />
                      <span className="checkbox-text">Available Today</span>
                    </label>
                    <label className="checkbox-label">
                      <input type="checkbox" className="checkbox" />
                      <span className="checkbox-text">Instant Response</span>
                    </label>
                    <label className="checkbox-label">
                      <input type="checkbox" className="checkbox" />
                      <span className="checkbox-text">FAANG Experience</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Enhanced Mentors Grid */}
      <section className="mentors-section">
        {filteredMentorships.length > 0 ? (
          <div className="mentors-grid">
            {filteredMentorships.map((mentorship) => (
              <div key={mentorship.id} className="mentor-card">
                {/* Card Header */}
                <div className="card-header">
                  {mentorship.activelyMentoring && (
                    <div className="availability-badge">
                      <div className="availability-dot"></div>
                      Available Now
                    </div>
                  )}

                  <button
                    onClick={() => toggleFavorite(mentorship.id)}
                    className="favorite-btn"
                  >
                    <Heart className={`heart-icon ${favorites.has(mentorship.id) ? 'favorited' : ''}`} />
                  </button>

                  <div className="mentor-info">
                    <div className="mentor-avatar-container">
                      <div className="mentor-avatar">
                        {mentorship.avatar}
                      </div>
                      <div className="online-indicator"></div>
                    </div>

                    <div className="mentor-details">
                      <h3 className="mentor-name">{mentorship.mentor}</h3>
                      <p className="mentor-role">{mentorship.role}</p>
                      
                      <div className="mentor-meta">
                        <div className="rating-info">
                          <Star className="star-icon" />
                          <span className="rating-value">{mentorship.rating}</span>
                          <span className="sessions-count">({mentorship.totalSessions})</span>
                        </div>
                        <div className="location-info">
                          <MapPin className="location-icon" />
                          {mentorship.location}
                        </div>
                      </div>

                      <div className="company-badge">
                        @{mentorship.company}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="card-content">
                  <p className="mentor-bio">{mentorship.bio}</p>

                  {/* Stats Grid */}
                  <div className="stats-grid">
                    <div className="stat-item">
                      <p className="stat-value">{mentorship.successRate}</p>
                      <p className="stat-label">Success Rate</p>
                    </div>
                    <div className="stat-item">
                      <p className="stat-value">{mentorship.responseTime}</p>
                      <p className="stat-label">Response Time</p>
                    </div>
                  </div>

                  {/* Expertise Tags */}
                  <div className="expertise-section">
                    <p className="expertise-label">Expertise:</p>
                    <div className="expertise-tags">
                      {mentorship.expertise.slice(0, 3).map((skill, index) => (
                        <span key={index} className="expertise-tag">
                          {skill}
                        </span>
                      ))}
                      {mentorship.expertise.length > 3 && (
                        <span className="expertise-tag more">
                          +{mentorship.expertise.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Next Available */}
                  <div className="availability-info">
                    <Clock className="clock-icon" />
                    <span className="availability-label">Next available: </span>
                    <span className="availability-time">{mentorship.nextAvailable}</span>
                  </div>

                  {/* Action Buttons */}
                  <div className="card-footer">
                    <div className="price-section">
                      <p className="price">₹{mentorship.price}</p>
                      <p className="price-label1">per session</p>
                    </div>
                    
                    <div className="action-buttons">
                      <button className="action-btn">
                        <MessageCircle className="action-icon" />
                      </button>
                      <button className="action-btn">
                        <Video className="action-icon" />
                      </button>
                      <button
                        onClick={() => handleBookSession(mentorship)}
                        className="book-btn"
                      >
                        Book Now
                        <ArrowRight className="arrow-icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <Users className="users-icon" />
            </div>
            <h3 className="empty-title">No mentors found</h3>
            <p className="empty-description">Try adjusting your search criteria or filters</p>
            <button
              onClick={() => {
                setActiveFilter("All");
                setSearchQuery("");
                setPriceRange([0, 5000]);
              }}
              className="reset-btn"
            >
              Reset Filters
            </button>
          </div>
        )}
      </section>

      {/* Enhanced Trust Indicators */}
      <section className="trust-section">
        <div className="trust-container">
          <div className="trust-features">
            <div className="trust-feature">
              <div className="feature-icon secure">
                <Shield className="icon" />
              </div>
              <h3 className="feature-title">100% Secure</h3>
              <p className="feature-description">Your data and payments are completely secure</p>
            </div>
            <div className="trust-feature">
              <div className="feature-icon guarantee">
                <Check className="icon" />
              </div>
              <h3 className="feature-title">Satisfaction Guaranteed</h3>
              <p className="feature-description">Money-back guarantee if you're not satisfied</p>
            </div>
            <div className="trust-feature">
              <div className="feature-icon support">
                <Users className="icon" />
              </div>
              <h3 className="feature-title">Expert Support</h3>
              <p className="feature-description">24/7 support from our dedicated team</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Booking Modal */}
      {showBookingModal && selectedMentor && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {bookingStep === 1 && "Book Your Session"}
                {bookingStep === 2 && "Confirm Booking"}
                {bookingStep === 3 && "Booking Confirmed!"}
              </h2>
              <button onClick={closeModal} className="close-btn">
                <X className="close-icon" />
              </button>
            </div>

            {bookingStep === 1 && (
              <div className="modal-body">
                {/* Mentor Summary */}
                <div className="mentor-summary">
                  <div className="summary-avatar">
                    {selectedMentor.avatar}
                  </div>
                  <div className="summary-info">
                    <h3 className="summary-name">{selectedMentor.mentor}</h3>
                    <p className="summary-role">{selectedMentor.role} @ {selectedMentor.company}</p>
                    <div className="summary-rating">
                      <Star className="summary-star" />
                      <span className="summary-rating-value">{selectedMentor.rating}</span>
                      <span className="summary-price">₹{selectedMentor.price}</span>
                      <span className="summary-price-unit">/session</span>
                    </div>
                  </div>
                </div>

                {/* Session Type Selection */}
                <div className="form-section">
                  <label className="form-label">Session Type</label>
                  <div className="session-types">
                    <button
                      onClick={() => setSessionType("1-on-1")}
                      className={`session-type-btn ${sessionType === "1-on-1" ? 'active' : ''}`}
                    >
                      <div className="session-icon">
                        <Users className="session-icon-svg" />
                      </div>
                      <div className="session-info">
                        <h4 className="session-title">1-on-1 Session</h4>
                        <p className="session-description">Personal mentoring session</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setSessionType("Mock Interview")}
                      className={`session-type-btn ${sessionType === "Mock Interview" ? 'active' : ''}`}
                    >
                      <div className="session-icon">
                        <Video className="session-icon-svg" />
                      </div>
                      <div className="session-info">
                        <h4 className="session-title">Mock Interview</h4>
                        <p className="session-description">Practice interview session</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Date Selection */}
                <div className="form-section">
                  <label className="form-label">Select Date</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="date-input"
                  />
                </div>

                {/* Time Selection */}
                <div className="form-section">
                  <label className="form-label">Available Time Slots</label>
                  <div className="time-slots">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`time-slot ${selectedTime === time ? 'active' : ''}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={proceedToNext}
                  disabled={!selectedDate || !selectedTime}
                  className="continue-btn"
                >
                  Continue to Payment
                </button>
              </div>
            )}

            {bookingStep === 2 && (
              <div className="modal-body">
                <div className="booking-summary">
                  <h3 className="summary-title">Booking Summary</h3>
                  
                  <div className="summary-details">
                    <div className="summary-row">
                      <span className="summary-label">Mentor:</span>
                      <span className="summary-value">{selectedMentor.mentor}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Session Type:</span>
                      <span className="summary-value">{sessionType}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Date:</span>
                      <span className="summary-value">{new Date(selectedDate).toLocaleDateString()}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Time:</span>
                      <span className="summary-value">{selectedTime}</span>
                    </div>
                    <div className="summary-row total">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-value total-amount">₹{selectedMentor.price}</span>
                    </div>
                  </div>
                </div>

                <div className="security-notice">
                  <div className="security-icon">
                    <Shield className="shield-icon" />
                  </div>
                  <div className="security-text">
                    <p className="security-title">Secure Payment</p>
                    <p className="security-description">Your payment is protected by bank-level security. You can reschedule up to 24 hours before the session.</p>
                  </div>
                </div>

                <div className="modal-actions">
                  <button onClick={() => setBookingStep(1)} className="back-btn">
                    Back
                  </button>
                  <button onClick={confirmBooking} className="confirm-btn">
                    Confirm & Pay
                  </button>
                </div>
              </div>
            )}

            {bookingStep === 3 && (
              <div className="modal-body success">
                <div className="success-icon">
                  <Check className="success-check" />
                </div>
                
                <div className="success-content">
                  <h3 className="success-title">Session Booked Successfully!</h3>
                  <p className="success-description">
                    You'll receive a confirmation email with meeting details and calendar invite.
                  </p>
                </div>

                <div className="booking-details">
                  <div className="detail-item">
                    <Calendar className="detail-icon" />
                    <div>
                      <p className="detail-title">{sessionType} with {selectedMentor.mentor}</p>
                      <p className="detail-subtitle">{new Date(selectedDate).toLocaleDateString()} at {selectedTime}</p>
                    </div>
                  </div>
                  <div className="detail-item">
                    <Video className="detail-icon" />
                    <div>
                      <p className="detail-title">Meeting Link</p>
                      <p className="detail-subtitle">Will be shared 30 minutes before the session</p>
                    </div>
                  </div>
                  <div className="detail-item">
                    <MessageCircle className="detail-icon" />
                    <div>
                      <p className="detail-title">Direct Chat</p>
                      <p className="detail-subtitle">Connect with your mentor anytime</p>
                    </div>
                  </div>
                </div>

                <div className="success-actions">
                  <button className="calendar-btn">
                    Add to Calendar
                  </button>
                  <button onClick={closeModal} className="done-btn">
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorshipPlatform;